import React, { useState, useEffect } from 'react';
import HighlightCard from './HighlightCard';
import API_URL from '../utils/api';
import { readCache, writeCache, clearCache } from '../utils/propertyCache';

const CACHE_KEY = 'highlights';

function filterHighlights(data) {
  return data.filter(p => {
    const sections   = p.sections || [p.section];
    const notExpired = !p.expiryDate || new Date(p.expiryDate) > new Date();
    return sections.includes('highlights') && p.status === 'available' && notExpired && p.isActive !== false;
  });
}

/**
 * TanaviHighlights
 *
 * Props:
 *   getPropertyRef(id, section) — ref factory from Home's propertyRefs map.
 *                                 Uses composite key (section:id) to handle
 *                                 same property appearing in multiple sections.
 */
const TanaviHighlights = ({ getPropertyRef }) => {
  // Seed from cache — cards render instantly on back-nav, no network wait
  const cachedData = readCache(CACHE_KEY);
  const [properties, setProperties] = useState(cachedData || []);

  useEffect(() => {
    let cancelled = false;

    const fetchHighlights = async () => {
      try {
        const res  = await fetch(`${API_URL}/api/properties`);
        const data = await res.json();
        if (cancelled) return;
        const filtered = filterHighlights(data);
        writeCache(CACHE_KEY, filtered);
        setProperties(filtered);
      } catch (err) {
        if (cancelled) console.error('Error fetching highlights:', err);
      }
    };

    // Always fetch silently — if cache was warm the UI is already showing,
    // this just keeps it fresh. No loading state, no skeleton flash.
    fetchHighlights();

    const interval = setInterval(fetchHighlights, 10_000); // Reduced from 60s to 10s
    
    // Also refresh when page becomes visible (user switches back to tab)
    // Clear cache first to ensure deleted properties are fetched
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        clearCache(CACHE_KEY);
        fetchHighlights();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Always render the section with fixed height to prevent layout shifts
  return (
    <section id="tanavi-highlights" className="py-4 md:py-6 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6">Tanavi Highlights</h2>
        <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6">
          {properties.length === 0 ? (
            <>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex-shrink-0 w-[calc(50%-8px)] snap-start md:w-auto">
                  <div className="bg-gray-200 rounded-lg animate-pulse h-[300px]" />
                </div>
              ))}
            </>
          ) : (
            properties.slice(0, 8).map(property => (
              <div
                key={property._id}
                className="flex-shrink-0 w-[calc(50%-8px)] snap-start md:w-auto"
              >
                <HighlightCard property={property} ref={getPropertyRef ? getPropertyRef(property._id, 'highlights') : null} />
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default TanaviHighlights;
